import { ManipulationType } from 'generated/graphql';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FeedbackLabel, FeedbackLabelType } from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';
import { useFeedbackOnFeedbackStore } from '../store';
import {
  getReasonPredictions,
  getSentimentPrediction,
  getTrackedKeywordPredictions,
} from '../utils';

interface UpdateSelectedLabelsOptions {
  feedbackID: UUID;
  selectedLabels: FeedbackLabel[];
  setSelectedLabels: React.Dispatch<React.SetStateAction<FeedbackLabel[]>>;
}

export const useUpdateSelectedLabels = ({
  feedbackID,
  selectedLabels,
  setSelectedLabels,
}: UpdateSelectedLabelsOptions) => {
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );
  const editedRecord = useMemo(
    () => editedRecords.find((editedRecord) => editedRecord.id === feedbackID),
    [editedRecords, feedbackID],
  );

  const editedReasonPredictions = useMemo(
    () => getReasonPredictions(editedRecord),
    [editedRecord],
  );
  const editedTrackedKeywordPredictions = useMemo(
    () => getTrackedKeywordPredictions(editedRecord),
    [editedRecord],
  );

  const editedSentiment = getSentimentPrediction(editedRecord)?.prediction;

  const filteredSelectedLabels = useMemo(
    () =>
      selectedLabels.filter(
        (label) =>
          !editedReasonPredictions?.find(
            (editedReasonPrediction) =>
              editedReasonPrediction.prediction.id === label.id &&
              editedReasonPrediction.manipulationType ===
                ManipulationType.Remove,
          ) &&
          !editedTrackedKeywordPredictions?.find(
            (editedTrackedKeywordPrediction) =>
              editedTrackedKeywordPrediction.prediction.id === label.id &&
              editedTrackedKeywordPrediction.manipulationType ===
                ManipulationType.Remove,
          ) &&
          (!editedSentiment ||
            (editedSentiment && label.type !== FeedbackLabelType.Sentiment)),
      ),
    [
      editedReasonPredictions,
      editedSentiment,
      editedTrackedKeywordPredictions,
      selectedLabels,
    ],
  );

  useEffect(() => {
    if (
      !filteredSelectedLabels.length ||
      isEqual(selectedLabels, filteredSelectedLabels)
    ) {
      return;
    }
    setSelectedLabels(filteredSelectedLabels);
  }, [filteredSelectedLabels, selectedLabels, setSelectedLabels]);
};
